import React from 'react';
import { Info } from 'lucide-react';
import { BarChart, Bar, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { riskScoreColour, type RiskScoreColour } from 'src/shared/components/risk-score-cell/riskScoreColour';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';

export type RiskDistributionBin = {
    lowerBound: number;
    upperBound: number;
    count: number;
};

type Props = {
    bins: RiskDistributionBin[];
    isLoading?: boolean;
};

const LEGEND_ITEMS: { label: string; colour: RiskScoreColour }[] = [
    { label: 'Very Low', colour: 'divergent-red-amber-green-5' },
    { label: 'Low', colour: 'divergent-red-amber-green-4' },
    { label: 'Medium', colour: 'divergent-red-amber-green-3' },
    { label: 'High', colour: 'divergent-red-amber-green-2' },
    { label: 'Very High', colour: 'divergent-red-amber-green-1' },
];

const BG_CLASS: Record<RiskScoreColour, string> = {
    'divergent-red-amber-green-1': 'bg-divergent-red-amber-green-1',
    'divergent-red-amber-green-2': 'bg-divergent-red-amber-green-2',
    'divergent-red-amber-green-3': 'bg-divergent-red-amber-green-3',
    'divergent-red-amber-green-4': 'bg-divergent-red-amber-green-4',
    'divergent-red-amber-green-5': 'bg-divergent-red-amber-green-5',
};

const FILL_CLASS: Record<RiskScoreColour, string> = {
    'divergent-red-amber-green-1': 'fill-divergent-red-amber-green-1',
    'divergent-red-amber-green-2': 'fill-divergent-red-amber-green-2',
    'divergent-red-amber-green-3': 'fill-divergent-red-amber-green-3',
    'divergent-red-amber-green-4': 'fill-divergent-red-amber-green-4',
    'divergent-red-amber-green-5': 'fill-divergent-red-amber-green-5',
};

const HistogramTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const count: number = payload[0]?.value ?? 0;
    return (
        <div className='rounded-md bg-white px-3 py-2 shadow-md text-xs text-muted-foreground border border-grey-20'>
            {count} {count === 1 ? 'site' : 'sites'}
        </div>
    );
};

const BarShape = (props: any) => {
    const { x, y, width, height, count, lowerBound } = props;
    const fillClass = count === 0 ? 'fill-muted' : FILL_CLASS[riskScoreColour(lowerBound, 'higher-worse')];
    return <rect x={x} y={y} width={width} height={height} className={fillClass} rx={4} ry={4} />;
};

const formatBound = (n: number) => (n === 0 ? '0' : n.toFixed(2));

const bandLabel = (lowerBound: number): string => `${formatBound(lowerBound)} – ${formatBound(lowerBound + 0.1)}`;

const RiskDistributionHistogram = ({ bins, isLoading }: Props) => {
    const chartData = bins;
    const groupedLabels = bins.filter((_, i) => i % 2 === 0);

    const maxCount = Math.max(...bins.map(b => b.count), 1);
    const yTicks = [1, 2, 3, 4, 5].map(i => (i * maxCount) / 5);

    return (
        <div>
            {/* Header: title + info on left, legend on right */}
            <div className='flex items-center justify-between mb-3'>
                <div className='flex items-center gap-2'>
                    <span className='text-xl text-primary'>Risk distribution across sites</span>
                    <QuestionButtonHelp
                        type='api'
                        placement='top'
                        slug='gnr-histogram-tooltip'
                        icon={<Info className='h-4 w-4 text-primary' />}
                    />
                </div>
                <div className='flex items-center gap-3'>
                    <span className='text-xs text-muted-foreground'>Risk:</span>
                    {LEGEND_ITEMS.map(({ label, colour }) => (
                        <div key={label} className='flex items-center gap-1'>
                            <span className={`inline-block h-2.5 w-2.5 rounded-sm ${BG_CLASS[colour]}`} />
                            <span className='text-xs text-muted-foreground'>{label}</span>
                        </div>
                    ))}
                </div>
            </div>

            {isLoading ? (
                <div className='h-[260px] animate-pulse bg-grey-20 rounded' data-testid='risk-distribution-skeleton' />
            ) : (
                <>
                    {/* Count labels — hidden visually, retained for accessibility */}
                    <div className='sr-only'>
                        {bins.map((bin, i) => (
                            <span key={i} data-testid='bin-count'>
                                {bin.count}
                            </span>
                        ))}
                    </div>

                    <ResponsiveContainer width='100%' height={180}>
                        <BarChart data={chartData} margin={{ top: 4, right: 5, bottom: 0, left: 5 }} barCategoryGap='5%'>
                            <CartesianGrid vertical={false} strokeDasharray='2 2' stroke='rgb(229, 229, 229)' />
                            <Tooltip content={<HistogramTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                            <XAxis dataKey='lowerBound' hide />
                            <YAxis hide ticks={yTicks} />
                            <Bar dataKey='count' minPointSize={5} shape={BarShape} />
                        </BarChart>
                    </ResponsiveContainer>

                    {/* 5-category coloured x-axis band */}
                    <div className='mt-2 flex gap-1 px-1.5' data-testid='risk-category-axis'>
                        {LEGEND_ITEMS.map(({ colour, label }) => (
                            <div
                                key={colour}
                                data-testid='risk-category-segment'
                                aria-label={label}
                                className={`flex-1 h-1.5 rounded-sm ${BG_CLASS[colour]}`}
                            />
                        ))}
                    </div>

                    {/* Grouped x-axis labels — one label per pair of 0.05 bins (covers a 0.10 range) */}
                    <div className='flex px-1.5'>
                        {groupedLabels.map((bin, i) => (
                            <span key={i} className='flex-[2] text-center text-xs text-muted-foreground pt-1'>
                                {bandLabel(bin.lowerBound)}
                            </span>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default RiskDistributionHistogram;
